/* eslint-disable default-param-last */
/* eslint-disable class-methods-use-this */
import { store } from '@/appContext';
import FILTERED_AUCTIONS_QUERY from '@/api/graphql/definitions/queries/filteredAuctionsQuery';
import COUNTS_QUERY from '@/api/graphql/definitions/queries/countsQuery';
import AUCTION_LIVE_UPDATES_SUBSCRIPTION from '@/api/graphql/definitions/subscriptions/auctionLiveUpdatesSubscription';
import GaiaGraphQLClient from '@/api/graphql/GaiaGraphQLClient';
import EasyPassClient from './EasyPassClient';
import SearchServiceClient from './SearchServiceClient';

class AuctionsClient {
  constructor(user) {
    this.easyPassClient = new EasyPassClient(user);
    this.searchServiceClient = new SearchServiceClient();
    this.rootUri = 'auctions';
    this.easyPassRootUri = 'api/acvapi/v3';
    this.customHeaders = {
      'json-naming-strategy': 'camel',
    };
    this.defaultPageSize = 12;
  }

  handleEasyPassResponse(response) {
    return {
      ...response.data.data,
    };
  }

  addToWatchList(savedAuctionId) {
    return this.searchServiceClient.addWatchlist(savedAuctionId);
  }

  getWatchList() {
    return this.easyPassClient.get(
      'bff/filters/auctions/buying/watchlist',
      null,
      this.customHeaders,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  removeFromWatchList(savedAuctionId) {
    return this.searchServiceClient.removeWatchlist(savedAuctionId);
  }

  getSavedSearches() {
    return this.searchServiceClient.getSavedSearches();
  }

  addSavedSearch(savedSearch) {
    return this.searchServiceClient.saveSavedSearch(savedSearch);
  }

  getSavedSearch(savedSearchId) {
    return this.searchServiceClient.getSavedSearch(savedSearchId);
  }

  updateSavedSearch(savedSearch) {
    return this.searchServiceClient.updateSavedSearch(savedSearch);
  }

  deleteSavedSearch(savedSearchId) {
    return this.searchServiceClient.deleteSavedSearch(savedSearchId);
  }

  getAuctionEndedCountMatchingFilter(filterData) {
    return this.searchServiceClient.getAuctionEndedCountsMatchingFilter(filterData);
  }

  getWonAuctionStats() {
    return this.easyPassClient.get('bff/auctions/buying/search/won/dashboard', null, this.customHeaders);
  }

  getMyNeedsFinalizing() {
    return this.easyPassClient.get(
      'bff/auctions/buying/search/myacv/needs_finalization',
      null,
      this.customHeaders,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  getMyPendingProxies() {
    return this.easyPassClient.get(
      'bff/auctions/buying/search/myacv/pending_proxy',
      null,
      this.customHeaders,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  getMyActiveAuctions() {
    return this.easyPassClient.get(
      'bff/auctions/buying/search/myacv/active',
      null,
      this.customHeaders,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  getMyNegotiations() {
    return this.easyPassClient.get(
      'bff/auctions/buying/search/myacv/negotiation',
      null,
      this.customHeaders,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  getAuctionsById(auctionIds, retries = 0) {
    return this.easyPassClient.post(
      'bff/auctions/buying/search/list_by_id',
      auctionIds,
      this.customHeaders,
      null,
      retries,
    )
      .then((response) => this.handleEasyPassResponse(response));
  }

  async getMarketplaceAuctions(params) {
    const paramsToUse = params;
    const { cancelToken } = paramsToUse;
    delete paramsToUse.cancelToken;

    const usingGraphQl = await store.dispatch(
      'getFlag',
      store.state.featureFlags.featureFlagSlugs.enableGraphqlOnSrp,
    );

    if (usingGraphQl) {
      const apiResponse = await GaiaGraphQLClient().query({
        operation: {
          query: FILTERED_AUCTIONS_QUERY,
          variables: {
            filters: paramsToUse.filters,
            savedSearchIds: paramsToUse.savedSearchIds,
            size: paramsToUse.size,
            sort: paramsToUse.sort,
          },
          context: {
            fetchOptions: {
              signal: cancelToken,
            },
          },
        },
      });

      return {
        results: apiResponse.data?.filteredAuctions,
      };
    }

    return this.easyPassClient
      .post(
        'bff/filters/auctions/buying/auctions',
        paramsToUse,
        this.customHeaders,
        cancelToken,
        paramsToUse.retries ?? 6,
      )
      .then((response) => this.handleEasyPassResponse(response));
  }

  async getMarketplaceCounts(params = {}) {
    const paramsToUse = params;
    const { cancelToken } = paramsToUse;
    delete paramsToUse.cancelToken;

    const usingGraphQl = await store.dispatch(
      'getFlag',
      store.state.featureFlags.featureFlagSlugs.enableGraphqlOnSrp,
    );

    if (usingGraphQl) {
      const apiResponse = await GaiaGraphQLClient().query({
        operation: {
          query: COUNTS_QUERY,
          variables: {
            filters: paramsToUse.filters,
            savedSearchIds: paramsToUse.savedSearchIds,
            privateLaneIds: paramsToUse.privateLaneIds,
          },
          context: {
            fetchOptions: {
              signal: cancelToken,
            },
          },
        },
      });

      return apiResponse.data?.counts;
    }

    const directToSearchService = await store.dispatch(
      'getFlag',
      store.state.featureFlags.featureFlagSlugs.routeCountsV3ToSearchServiceDirectly,
    );

    if (directToSearchService) {
      return this.searchServiceClient.getAuctionCounts(paramsToUse);
    }

    return this.easyPassClient
      .post(
        'bff/filters/auctions/buying/counts_v2',
        paramsToUse,
        this.customHeaders,
        cancelToken,
      )
      .then((response) => this.handleEasyPassResponse(response));
  }

  async subscribeAuctionsByIds({
    auctionIds,
    callbackFn,
    handleSubscriptionFn,
  }) {
    const usingGraphQl = await store.dispatch(
      'getFlag',
      store.state.featureFlags.featureFlagSlugs.enableGraphqlOnSrp,
    );

    if (usingGraphQl) {
      GaiaGraphQLClient().subscribe({
        operation: {
          query: AUCTION_LIVE_UPDATES_SUBSCRIPTION,
          variables: {
            auction_ids: auctionIds,
          },
        },
        callbackFn,
        handleSubscriptionFn,
      });
    }
  }

  async unsubscribeAuctionSubscription({ subscriptionId }) {
    const usingGraphQl = await store.dispatch(
      'getFlag',
      store.state.featureFlags.featureFlagSlugs.enableGraphqlOnSrp,
    );

    if (usingGraphQl) {
      GaiaGraphQLClient().unsubscribe(subscriptionId);
    }
  }
}

export default AuctionsClient;
